import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getAdminUser, updateAdminUser } from '../../../api/admin'
import { Button } from '../../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../../components/ui/Card'
import { useConfirm } from '../../../components/ui/ConfirmModal'
import { useToast } from '../../../components/ui/Toast'
import { useFetch } from '../../../hooks/useFetch'
import { infoLabelClass, infoValueClass } from '../../../lib/tableStyles'
import type { AdminUser } from '../../../types/admin'
import type { UserRole } from '../../../types/auth'

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  staff: 'Staff',
  client: 'Client',
  user: 'User',
}

function InfoField({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 mb-4">
      <span className={infoLabelClass}>{title}</span>
      <span className={infoValueClass}>{children}</span>
    </div>
  )
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()
  const [busy, setBusy] = useState(false)

  const { data: user, loading, error, refetch } = useFetch(
    () => getAdminUser(Number(id)),
    [id],
  )

  const isSuperAdmin = user?.role === 'super_admin'

  const doAction = async (action: () => Promise<AdminUser>, successMsg: string) => {
    setBusy(true)
    try {
      await action()
      toast.success(successMsg)
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Action failed.')
    } finally {
      setBusy(false)
    }
  }

  const handleToggleActive = async () => {
    if (!user) return
    const willDeactivate = user.is_active
    const ok = await confirm({
      title: willDeactivate ? 'Deactivate user?' : 'Activate user?',
      message: willDeactivate
        ? `${user.full_name ?? user.email} will be unable to log in.`
        : `${user.full_name ?? user.email} will be able to log in again.`,
      confirmLabel: willDeactivate ? 'Deactivate' : 'Activate',
    })
    if (!ok) return
    await doAction(
      () => updateAdminUser(user.id, { is_active: !user.is_active }),
      willDeactivate ? 'User deactivated.' : 'User activated.',
    )
  }

  const handleVerifyEmail = async () => {
    if (!user) return
    const ok = await confirm({
      title: 'Verify email manually?',
      message: `Mark ${user.email} as verified without sending a verification link.`,
      confirmLabel: 'Verify',
    })
    if (!ok) return
    await doAction(
      () => updateAdminUser(user.id, { email_verified: true }),
      'Email marked as verified.',
    )
  }

  const handleChangeRole = async (newRole: UserRole) => {
    if (!user) return
    await doAction(
      () => updateAdminUser(user.id, { role: newRole }),
      `Role changed to ${ROLE_LABELS[newRole]}.`,
    )
  }

  if (loading) {
    return (
      <div className="p-8 text-text-2">Loading…</div>
    )
  }

  if (error) {
    return <ErrorBox>{error}</ErrorBox>
  }

  if (!user) return null

  return (
    <div className="max-w-[760px]">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => void navigate(-1)}
          className="bg-transparent border-none cursor-pointer text-accent text-[0.875rem] font-medium p-0"
        >
          ← Back
        </button>
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">
          {user.full_name ?? user.email}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-5 max-sm:grid-cols-1">
        {/* User info */}
        <Card>
          <SectionTitle>Profile</SectionTitle>
          <InfoField title="Full name">{user.full_name ?? '—'}</InfoField>
          <InfoField title="Email">{user.email}</InfoField>
          <InfoField title="Role">{ROLE_LABELS[user.role] ?? user.role}</InfoField>
          <InfoField title="Status">{user.is_active ? 'Active' : 'Inactive'}</InfoField>
          <InfoField title="Email verified">{user.email_verified ? 'Yes' : 'No'}</InfoField>
          <InfoField title="Joined">{new Date(user.created_at).toLocaleDateString()}</InfoField>
        </Card>

        {/* Actions */}
        <Card>
          <SectionTitle>Actions</SectionTitle>

          {isSuperAdmin ? (
            <p className="text-[0.875rem] text-text-3 italic">
              Super admin accounts cannot be modified here.
            </p>
          ) : (
            <div className="flex flex-col gap-3">

              {/* Activate / Deactivate */}
              <div>
                <div className="text-[0.8rem] font-semibold text-text-2 mb-[0.35rem]">Account status</div>
                <Button
                  variant={user.is_active ? 'danger' : 'outline'}
                  size="sm"
                  disabled={busy}
                  onClick={() => void handleToggleActive()}
                >
                  {user.is_active ? 'Deactivate account' : 'Activate account'}
                </Button>
              </div>

              {/* Verify email */}
              {!user.email_verified && (
                <div>
                  <div className="text-[0.8rem] font-semibold text-text-2 mb-[0.35rem]">Email verification</div>
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleVerifyEmail()}>
                    Mark email as verified
                  </Button>
                </div>
              )}

              {/* Change role */}
              <div>
                <div className="text-[0.8rem] font-semibold text-text-2 mb-[0.35rem]">Change role</div>
                <div className="flex gap-2 flex-wrap">
                  {(['user', 'staff', 'client'] as UserRole[]).filter(r => r !== user.role).map(r => (
                    <Button key={r} variant="outline" size="sm" disabled={busy} onClick={() => void handleChangeRole(r)}>
                      Set to {ROLE_LABELS[r]}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}
